import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { queryKeys, useAuthenticatedClient } from '@/api/queries'
import type { components } from '@/api/schema'
import { getApiErrorMessage } from '@/lib/api-error'
import { useBlobUrl } from '@/lib/use-blob-url'
import type { StatusTone } from '@/lib/status-tones'

// Warranty claims customers file from their order tracker (USA-915): the Claims list, and the claims on a
// purchase order's and a customer's pages, where staff work them.

export type WarrantyClaim = components['schemas']['WarrantyClaimRead']
export type WarrantyClaimStatus = WarrantyClaim['status']
export type WarrantyClaimUpdate = components['schemas']['WarrantyClaimUpdate']
export type WarrantyClaimFilter = WarrantyClaimStatus | 'open' | 'all'
type ClaimPage = components['schemas']['PaginatedResponse_WarrantyClaimRead_']
/** One warranty item in full, for the PO action cards (USA-915). */
export type WarrantyClaimAction = components['schemas']['WarrantyClaimActionRead']

export const WARRANTY_CLAIM_STATUS: Record<WarrantyClaimStatus, { label: string; tone: StatusTone }> = {
  submitted: { label: 'New', tone: 'warning' },
  with_manufacturer: { label: 'With manufacturer', tone: 'progress' },
  approved: { label: 'Approved', tone: 'progress' },
  resolved: { label: 'Resolved', tone: 'success' },
  declined: { label: 'Not covered', tone: 'danger' },
}

export const WARRANTY_CLAIM_FILTERS = [
  'open',
  'submitted',
  'with_manufacturer',
  'approved',
  'resolved',
  'declined',
  'all',
] as const satisfies readonly WarrantyClaimFilter[]

export const warrantyClaimKeys = {
  all: ['warranty-claims'] as const,
  list: (status: WarrantyClaimFilter, search: string, page: number) =>
    ['warranty-claims', 'list', status, search, page] as const,
  purchaseOrder: (poUuid: string) => ['warranty-claims', 'purchase-order', poUuid] as const,
  customer: (groupUuid: string) => ['warranty-claims', 'customer', groupUuid] as const,
  photo: (claimUuid: string, photoUuid: string) => ['warranty-claims', 'photo', claimUuid, photoUuid] as const,
  /** A claim's items in full, cached as one record with an ``actions`` list like a PO's. */
  claimActions: (claimUuid: string) => ['warranty-claims', 'claim-actions', claimUuid] as const,
  /** One item no claim holds, cached the same way. */
  action: (actionUuid: string) => ['warranty-claims', 'action', actionUuid] as const,
}

/** Refetch every claim list and warranty item on screen (not the photos, which never change). */
export function invalidateWarrantyClaims(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    queryKey: warrantyClaimKeys.all,
    predicate: (query) => query.queryKey[1] !== 'photo',
  })
}

export function useWarrantyClaims(status: WarrantyClaimFilter, search: string, page: number) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: warrantyClaimKeys.list(status, search, page),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/warranty-claims', {
        params: { query: { status, search: search || undefined, page } },
      })
      if (error) throw error
      return data
    },
    placeholderData: keepPreviousData,
  })
}

export function usePurchaseOrderWarrantyClaims(poUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: warrantyClaimKeys.purchaseOrder(poUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/purchase-orders/{po_uuid}/warranty-claims', {
        params: { path: { po_uuid: poUuid } },
      })
      if (error) throw error
      return data
    },
  })
}

export function useCustomerWarrantyClaims(groupUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: warrantyClaimKeys.customer(groupUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/customer-groups/{group_uuid}/warranty-claims', {
        params: { path: { group_uuid: groupUuid } },
      })
      if (error) throw error
      return data
    },
  })
}

/**
 * A claim's items in full for the PO action cards (open, then closed in the last 30 days), in one request.
 * The record mirrors a PO's ``actions`` so the cards' optimistic updates apply to it.
 */
export function useWarrantyClaimActions(claimUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: warrantyClaimKeys.claimActions(claimUuid),
    queryFn: async (): Promise<{ actions: WarrantyClaimAction[] }> => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/warranty-claims/{claim_uuid}/actions', {
        params: { path: { claim_uuid: claimUuid } },
      })
      if (error) throw error
      return data
    },
  })
}

/** One item no claim holds yet (warranty@ mail nobody could tie to a claim), cached like a claim's items. */
export function useWarrantyAction(actionUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: warrantyClaimKeys.action(actionUuid),
    queryFn: async (): Promise<{ actions: WarrantyClaimAction[] }> => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/warranty-claims/actions/{action_uuid}', {
        params: { path: { action_uuid: actionUuid } },
      })
      if (error) throw error
      return { actions: [data] }
    },
  })
}

/** A customer's photo, fetched with the staff token. */
export function useWarrantyClaimPhoto(claimUuid: string, photoUuid: string) {
  return useBlobUrl(
    warrantyClaimKeys.photo(claimUuid, photoUuid),
    `/dashboard/warranty-claims/${claimUuid}/photos/${photoUuid}`,
    'This photo is no longer available.',
    'This photo did not load.',
  )
}

type ClaimsData = WarrantyClaim[] | ClaimPage

/** Replace one claim wherever it is listed: the Claims list, a PO's or a customer's claims. */
function patchClaims(queryClient: QueryClient, uuid: string, patch: (claim: WarrantyClaim) => WarrantyClaim) {
  const map = (claims: WarrantyClaim[]) => claims.map((claim) => (claim.uuid === uuid ? patch(claim) : claim))
  queryClient.setQueriesData<ClaimsData>({ queryKey: warrantyClaimKeys.all }, (data) =>
    Array.isArray(data) ? map(data) : data && 'items' in data ? { ...data, items: map(data.items) } : data,
  )
}

/** Change a claim; the change shows at once and rolls back if the save fails. */
export function useUpdateWarrantyClaim() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ uuid, update }: { uuid: string; update: WarrantyClaimUpdate }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/warranty-claims/{claim_uuid}', {
        params: { path: { claim_uuid: uuid } },
        body: update,
      })
      if (error) throw error
      return data
    },
    onMutate: async ({ uuid, update }) => {
      await queryClient.cancelQueries({ queryKey: warrantyClaimKeys.all })
      const previous = queryClient.getQueriesData<ClaimsData>({ queryKey: warrantyClaimKeys.all })
      patchClaims(queryClient, uuid, (claim) => ({
        ...claim,
        ...(update.status ? { status: update.status } : {}),
        ...('manufacturer_claim_number' in update
          ? { manufacturer_claim_number: update.manufacturer_claim_number }
          : {}),
        ...('customer_note' in update ? { customer_note: update.customer_note } : {}),
      }))
      return { previous }
    },
    onError: (error, _vars, context) => {
      for (const [key, data] of context?.previous ?? []) queryClient.setQueryData(key, data)
      toast.error(getApiErrorMessage(error, 'The claim did not save.'))
    },
    onSuccess: (saved) => patchClaims(queryClient, saved.uuid, () => saved),
    onSettled: () => {
      invalidateWarrantyClaims(queryClient)
      // Closing a claim closes its action items.
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
    },
  })
}

/** Send a claim that couldn't go out on its own (no manufacturer contact then, or the email failed). */
export function useSendWarrantyClaim() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (uuid: string) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/warranty-claims/{claim_uuid}/send-to-manufacturer', {
        params: { path: { claim_uuid: uuid } },
      })
      if (error) throw error
      return data
    },
    onSuccess: (saved) => {
      patchClaims(queryClient, saved.uuid, () => saved)
      if (!saved.manufacturer_thread_uuid) toast.error('The claim still could not be sent. See its action item.')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'The claim did not send.')),
    onSettled: (saved) => {
      invalidateWarrantyClaims(queryClient)
      // Sending closes its "Claim not sent" item.
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      if (saved?.po_uuid) void queryClient.invalidateQueries({ queryKey: queryKeys.communications.byPO(saved.po_uuid) })
    },
  })
}
